"use client";

/**
 * Compact Discord entry points for the member dashboard chrome — the full
 * connect/disconnect card stays in Profile → Contact (DiscordCard).
 *
 *   pill — top bar, beside the profile menu. "Connect Discord" while unlinked;
 *          "Join Discord server" while linked but not in the server; otherwise
 *          gone, so the bar stays clean.
 *   row  — profile dropdown and home card. Shows the connected state too, with
 *          a link into the server (or a "join" nudge if they aren't in it).
 *
 * Connect opens Discord in a new tab (the Hub stays put); the pill listens for
 * that tab's result so the top bar updates without a reload. All of it hides
 * until /discord/config says linking is configured.
 */

import { Check, ChevronRight, ExternalLink } from "lucide-react";
import { useWorkspace } from "@/lib/context/WorkspaceContext";
import {
    DISCORD_RETURN_PATH,
    startDiscordLink,
    useDiscordConfig,
    useDiscordLinkEvents,
    useDiscordMembership,
    watchForDiscordJoin,
} from "@/lib/discord";
import { cn } from "@/lib/utils";
import { DiscordMark } from "./DiscordMark";

export function DiscordConnectButton({
    variant,
    className,
}: {
    variant: "pill" | "row";
    className?: string;
}) {
    const { user, refreshUser } = useWorkspace();
    const config = useDiscordConfig();
    const linked = Boolean(user?.discord_verified_at);
    const { data } = useDiscordMembership(user?.id, linked && Boolean(config?.configured));

    // Only the pill listens — it is on every dashboard page, and one refresh per
    // link result is enough (the profile card, when open, handles its own banner).
    useDiscordLinkEvents((event) => {
        if (variant === "pill" && event.type === "linked") void refreshUser();
    });

    if (!user || !config?.configured) return null;

    const connect = () => startDiscordLink(DISCORD_RETURN_PATH, { newTab: true });
    const notInServer = linked && data?.in_guild === false;
    const invite = config.invite_url || config.server_url || null;
    const server = config.server_url || config.invite_url || null;

    if (variant === "pill") {
        if (notInServer && invite) {
            return (
                <a
                    href={invite}
                    target="_blank"
                    rel="noreferrer noopener"
                    onClick={() => watchForDiscordJoin()}
                    title="Join the CMT Discord server to get your roles"
                    className={cn(
                        "flex items-center gap-2 rounded-xl bg-amber-500 px-2.5 py-2 text-xs font-bold text-white transition-opacity hover:opacity-90 sm:px-3",
                        className,
                    )}
                >
                    <DiscordMark fill="#fff" />
                    <span className="hidden sm:inline">Join Discord server</span>
                </a>
            );
        }
        if (linked) return null;
        return (
            <button
                onClick={connect}
                title="Connect your Discord account"
                className={cn(
                    "flex items-center gap-2 rounded-xl bg-[#5865F2] px-2.5 py-2 text-xs font-bold text-white transition-opacity hover:opacity-90 sm:px-3",
                    className,
                )}
            >
                <DiscordMark fill="#fff" />
                <span className="hidden sm:inline">Connect Discord</span>
            </button>
        );
    }

    if (linked) {
        return (
            <div className={cn("flex items-center gap-2.5 rounded-[10px] px-3 py-2.5", className)}>
                <DiscordMark />
                <span className="min-w-0 flex-1 truncate text-xs font-semibold text-[#333]">
                    {user.discord_username ? `@${user.discord_username}` : "Discord"}
                </span>
                {notInServer && invite ? (
                    <a
                        href={invite}
                        target="_blank"
                        rel="noreferrer noopener"
                        onClick={() => watchForDiscordJoin()}
                        className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800 hover:bg-amber-100"
                    >
                        Join server <ExternalLink className="h-3 w-3" />
                    </a>
                ) : (
                    <>
                        {server && (
                            <a
                                href={server}
                                target="_blank"
                                rel="noreferrer noopener"
                                title="Open the CMT Discord server"
                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[#3c45a5] hover:underline"
                            >
                                Open <ExternalLink className="h-3 w-3" />
                            </a>
                        )}
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700">
                            <Check className="h-3 w-3" /> Connected
                        </span>
                    </>
                )}
            </div>
        );
    }

    return (
        <button
            onClick={connect}
            className={cn(
                "flex w-full items-center gap-2.5 rounded-[10px] bg-[#5865F2]/[0.08] px-3 py-2.5 text-left transition-colors hover:bg-[#5865F2]/[0.14]",
                className,
            )}
        >
            <DiscordMark />
            <span className="flex-1 text-xs font-semibold text-[#3c45a5]">Connect Discord</span>
            <ChevronRight className="size-4 shrink-0 text-[#5865F2]" />
        </button>
    );
}

export default DiscordConnectButton;
